import { t } from 'i18next'
import { cloneDeep } from 'lodash'
import React, { useState, useEffect } from 'react'
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { emptyGuid } from '../../utils/emptyGuid'
import { ButtonWithLoadingState } from '../Buttons'
import FileInput from '../Forms/FileInput'
import LanguageForm from '../Forms/LanguageForm'
import { FileOutput } from '../Forms/FileOutput'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { makeCall } from '../../utils/api/makeCall'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'

const documentTemplateObj = {
  description: {
    values: []
  },
  id: emptyGuid,
}
const UploadDocumentModal = ({ handleClose, objectId, threadId, documentData, fileTypes, useFileUpload = true, useDefaultThread=false }) => {
  const [document, setDocument] = useState(documentTemplateObj)
  const [files, setFiles] = useState([])
  const [uploadURL, setUploadURL] = useState('')
  const [acceptedFileTypes, setAcceptedFileTypes] = useState(null)
  const [errorStates, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [checkCloseWindow, setCheckCloseWindow] = useState(false)

  useMountEffect(() => {
    if (documentData) {
      setDocument(documentData)
    }
    if (fileTypes) {
      fileTypes.forEach(type => {
        if (type != "application/octet-stream") {
          setAcceptedFileTypes(prev => ({
            ...prev,
            [type]: []
          }))
        }
      });
    }
  })

  const updateNames = (translations) => {
    setDocument(prev => ({
      ...prev,
      description: {
        ...prev.description,
        values: translations
      }
    }))
  }
  async function uploadDocument(objectId, threadId, predecessorId, description, files, uploadURL, useDefaultThread) {
    let params = { objectId: objectId, threadId: threadId, predecessorId: predecessorId, description: description, uploadURL: uploadURL, useDefaultThread : useDefaultThread }
    await makeCall('uploadDocument', async () => {
      await apiClient.postFile(APIEndpoints.uploadOGISDocumentsAdmin, { data: JSON.stringify(params) }, files)
    }, setErrorStates, setLoadingStates)
  }
  async function updateDocumentDescription(documentId, description) {
    await makeCall('updateDocumentDescription', async () => {
      await apiClient.patchJson(APIEndpoints.documentAdmin(documentId).documents, description)
    }, setErrorStates, setLoadingStates)
  }
  const handleSave = async () => {
    if (useFileUpload) {
      await uploadDocument(objectId, threadId, document?.id, document?.description, files, uploadURL, useDefaultThread)
    }
    else {
      await updateDocumentDescription(document.id, document?.description)
    }
    if (errorStates.uploadDocument || errorStates.updateDocumentDescription) {
      return false
    }
    setCheckCloseWindow(true)
  }

  useEffect(() => {
    if (checkCloseWindow) {
      if (errorStates.uploadDocument || errorStates.updateDocumentDescription) {
        setCheckCloseWindow(false)
      }
      else {
        handleClose(true)
      }
    }
  }, [checkCloseWindow, errorStates, setCheckCloseWindow, handleClose])
  return (
    <div>
      {/* enforceFocus for OverlayRight with overLayZIndex with inputs  */}
      <Modal show={true} size="lg" onHide={handleClose} enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>{!!useFileUpload ? t('documentAdmin:addDocument') : ""}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LanguageForm
            languageTitle={t('documentAdmin:description')}
            languageArray={document?.description?.values ?? []}
            onChange={updateNames}
            hideFooter
          />
          <Form>
            {!!useFileUpload &&
              <>
                <div className="mb-3">
                  {loadingStates.uploadDocument
                    ? <div className="d-flex align-items-center justify-content-center"><Spinner animation='border' variant='primary' /></div>
                    : <>
                      <Form.Group className="mb-3">
                        <Form.Label className="d-block ps-1">URL</Form.Label>
                        <Form.Control
                          id="URL"
                          name="URL"
                          placeholder={"URL"}
                          value={uploadURL}
                          onChange={(e) => setUploadURL(e.target.value)}
                        />
                      </Form.Group>
                      <FileInput
                        maxFiles={1}
                        accept={acceptedFileTypes ? acceptedFileTypes : false}
                        onDrop={(acceptedFiles) => {
                          let newFiles = acceptedFiles.map(file => Object.assign(file, {
                            preview: URL.createObjectURL(file)
                          }));
                          setFiles(newFiles)
                        }}
                        isInvalid={false}
                      />
                    </>
                  }
                </div>
                {files.map((file, index) => (
                  <FileOutput
                    style={{ height: "4rem", borderRadius: "1rem", padding: "0.5rem" }}
                    className="mb-2"
                    key={`${file.name}_${index}`}
                    file={file}
                    deleteFile={
                      () => setFiles(prev => {
                        prev.splice(index, 1)
                        return cloneDeep(prev)
                      })
                    }
                  />))}
                {errorStates.uploadDocument && <Alert variant="danger">{errorStates.uploadDocument.value}</Alert>}
              </>
            }
            {errorStates.updateDocumentDescription && <Alert variant="danger">{errorStates.updateDocumentDescription.value}</Alert>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleClose()}>
            {t('cancelButton')}
          </Button>
          <ButtonWithLoadingState
            disabled={useFileUpload ? !files : false}
            buttonText={t('saveButton')}
            isLoading={loadingStates.uploadDocument}
            onClick={handleSave}
          />
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default UploadDocumentModal